import { hasValue } from 'src/packages/shared/utils/common';

import { getNearestSegment, intersectRects, interpolateOnNearestSegment } from './screencastGeometry';
import type { RectGeometry } from './screencastGeometry';
import type { Area, ResolvedHighlightArea, ResolvedTooltip, ScreencastTooltips, ResolvedTooltipLine } from './types';

type TooltipResolveState = {
  lines: ResolvedTooltipLine[];
  highlightAreas: ResolvedHighlightArea[];
  hasMatchedArea: boolean;
};

export function resolveTooltipAt(
  tooltips: ScreencastTooltips | null | undefined,
  x: number,
  y: number,
): ResolvedTooltip | null {
  if (!tooltips || !Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  const result: TooltipResolveState = {
    lines: [],
    highlightAreas: [],
    hasMatchedArea: false,
  };

  for (const area of tooltips.areas) {
    collectArea(area, x, y, 0, result, null);
  }

  if (!result.hasMatchedArea) {
    return null;
  }

  return {
    x,
    y,
    lines: result.lines,
    highlightAreas: result.highlightAreas,
  };
}

// Рекурсивно обходит дерево областей: если курсор попал в текущую область,
// добавляет ее в matched/highlight, формирует строки тултипа и затем
// продолжает обход дочерних областей с увеличенной глубиной вложенности.
function collectArea(
  area: Area,
  x: number,
  y: number,
  depth: number,
  result: TooltipResolveState,
  clipRect: RectGeometry | null,
): void {
  if (!containsPoint(area, x, y, clipRect)) {
    return;
  }

  const nextClipRect = getNextClipRect(area, clipRect);
  if (area.kind === 'rect' && !nextClipRect) {
    return;
  }

  result.hasMatchedArea = true;

  if (area.highlight) {
    result.highlightAreas.push({
      area,
      clipRect: area.kind === 'rect' ? nextClipRect : clipRect,
    });
  }

  if (typeof area.text === 'string' && area.text.length > 0) {
    result.lines.push({
      kind: 'text',
      depth,
      text: area.text,
      syntax: area.syntax,
    });
  }

  if (area.kind === 'polyline' && area.values.length > 0) {
    for (const distribution of area.values) {
      if (!distribution.canInterpolate) {
        continue;
      }

      const value = interpolateOnNearestSegment(area.segments, distribution.values, x, y, clipRect);

      if (value === null) {
        continue;
      }

      const formattedValue = value.toLocaleString(undefined, {
        minimumFractionDigits: distribution.minimumFractionDigits,
        maximumFractionDigits: distribution.maximumFractionDigits,
      });

      result.lines.push({
        kind: 'value',
        depth: depth + 1,
        syntax: area.syntax,
        value,
        formattedValue,
        distribution,
      });
    }
  }

  for (const childArea of area.areas) {
    collectArea(childArea, x, y, depth + 1, result, nextClipRect);
  }
}

function getNextClipRect(area: Area, clipRect: RectGeometry | null): RectGeometry | null {
  if (area.kind !== 'rect') {
    return clipRect;
  }

  return clipRect ? intersectRects(clipRect, area.rect) : area.rect;
}

function containsPoint(area: Area, x: number, y: number, clipRect: RectGeometry | null): boolean {
  if (area.kind === 'rect') {
    const visibleRect = clipRect ? intersectRects(area.rect, clipRect) : area.rect;
    if (!visibleRect) {
      return false;
    }

    const [xmin, ymin, xmax, ymax] = visibleRect;
    return x >= xmin && x <= xmax && y >= ymin && y <= ymax;
  }

  const distance = getNearestSegment(area.segments, x, y, clipRect)?.distance;

  if (!hasValue(distance)) {
    return false;
  }

  return distance <= area.radius;
}
